/** 商家店铺时区(IANA 名),以及按它读表的几个纯函数。
 *
 * 为什么要有这个:排期落库的是 `scheduled_date` + `scheduled_time`,**不带时区**,
 * 全链路的约定是"HH:mm 就是店铺本地时间" —— chatbot-api 的 MarketingDuePublishService
 * 用 shop.ianaTimezone 把这两个字段拼成真实时刻(slotInstant),yepairag 自己在注释里
 * 声明它不知道店铺时区、只回一个超集。这个约定后端全线遵守,唯独前端的写入端(排期卡)
 * 一直用浏览器时区算"今天/现在",于是同事在悉尼排的表被纽约的店按纽约时间执行,
 * 差 14 小时且跨天。
 *
 * 数据源 = chatbot-api 的 agent status roster(`timezone` 字段,来自 MerchantTimezoneService,
 * 后端也是拿它判定发布时刻)—— 和执行端同源,读写两端不会各说各话。
 *
 * 失败语义:拿不到时 storeTz=null。**显示端**回落浏览器时区无妨(顶多标签写错),
 * **写入端必须等 ready** —— 在时区到位前让商家点下 Confirm,写进去的又是浏览器时区的日期。
 */
import { useEffect, useState } from 'react';

import { getAgentStatusRoster } from '@lib/services/storeManager/agentTaskApi';
import { useAuthStore } from '@stores/authStore';

const cache = new Map<string, Promise<string | null>>();

function fetchZone(userId: string): Promise<string | null> {
  const hit = cache.get(userId);
  if (hit) return hit;
  const p = getAgentStatusRoster(userId)
    .then(r => r.timezone ?? null)
    .catch(() => {
      // 失败不缓存(同 useFacebookPages):一次 503 把 null 冻到整页刷新为止,
      // 会让排期卡的 Confirm 一直禁用着,看起来像卡片坏了。
      cache.delete(userId);
      return null;
    });
  cache.set(userId, p);
  return p;
}

/**
 * @returns storeTz — IANA 名,未就绪或商家没同步过时为 null;
 *          ready — 请求已回(无论成没成),写入端用它决定能不能提交。
 */
export function useStoreTimezone(): { storeTz: string | null; ready: boolean } {
  // selector 形式(不是 `const { user } = useAuthStore()`):CalendarTab 一系的测试把这个
  // store mock 成只接 selector 的函数,无参调用会当场 `sel is not a function`。
  const userId = useAuthStore(s => s.user?.id);
  const [storeTz, setStoreTz] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    fetchZone(userId).then(tz => {
      if (!cancelled) {
        setStoreTz(tz);
        setReady(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [userId]);
  return { storeTz, ready };
}

/**
 * 店铺时区的"现在",装在一个**本地** Date 里。
 *
 * 返回的 Date 不代表那个时刻 —— 它是店铺的墙上时钟(年/月/日/时/分)搬进本地时区的容器。
 * 这正是排期要的东西:排期卡里全是墙上时钟算术("今天几号"、"这周一是几号"、"这个槽过了没"),
 * 用它当 `new Date()` 的替身,下游算术一行都不用改。
 *
 * 不要拿它做跨时区的时刻比较 —— 那要的是真实 instant,这个不是。
 */
export function nowInZone(tz?: string | null): Date {
  if (!tz) return new Date();
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      // h23 而不是 hour12:false —— 后者在部分实现里把午夜给成 "24",
      // CalendarTab 的 Publish Now 就为此专门打了个 `=== '24'` 的补丁。
      hourCycle: 'h23',
    }).formatToParts(new Date());
    const get = (t: string) => Number(parts.find(x => x.type === t)?.value);
    const [y, m, d, h, min] = [get('year'), get('month'), get('day'), get('hour'), get('minute')];
    if ([y, m, d, h, min].some(n => Number.isNaN(n))) return new Date();
    return new Date(y, m - 1, d, h, min);
  } catch {
    // 无法识别的时区名(旧浏览器 / 脏数据)不该让整张卡白屏。
    return new Date();
  }
}

/** 店铺时区的今天,'YYYY-MM-DD'。 */
export function todayIsoInZone(tz?: string | null): string {
  const d = nowInZone(tz);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
}

/** 店铺时区的此刻钟点,'HH:mm'。 */
export function nowHHMMInZone(tz?: string | null): string {
  const d = nowInZone(tz);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
